import React, { useState } from "react";
import { AlertCircle, FileText, Play, RotateCw, Info } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const API = `${BACKEND_URL}/api`;

export default function Phase6({ session, refresh }) {
  const [downloading, setDownloading] = useState(false);
  const [running, setRunning] = useState(false);
  const p6 = session.phase_results?.phase6;

  const handleRunPhase6 = async () => {
    try {
      setRunning(true);
      const token = localStorage.getItem("google_token");
      const res = await fetch(`${API}/audit/session/${session.id}/phase6`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || data.detail || "Phase 6 calculation failed");
      }
      toast.success("Phase 6 settlement calculated successfully");
      if (refresh) refresh();
    } catch (e) {
      toast.error(`Phase 6 failed: ${e.message}`);
      if (refresh) refresh();
    } finally {
      setRunning(false);
    }
  };

  const downloadPdf = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem("google_token");
      const res = await fetch(`${API}/audit/session/${session.id}/partner-statement.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Settlr_Statement_${(session.property_name || "partner").replace(/ /g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading(false);
    }
  };

  if (!p6) {
    return (
      <div className="p-6 text-center border rounded-lg bg-card shadow-sm space-y-4 my-2">
        <div className="space-y-1">
          <h3 className="font-semibold text-base">Phase 6 — Partner Settlement</h3>
          <p className="text-sm text-muted-foreground">
            Phase 6 has not been run yet. Run Phase 5 successfully first, then click "Run Phase 6".
          </p>
        </div>
        <Button
          onClick={handleRunPhase6}
          disabled={running}
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          {running ? (
            <>
              <RotateCw className="h-4 w-4 mr-2 animate-spin" />
              Running Phase 6...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2 fill-current" />
              Run Phase 6 Settlement
            </>
          )}
        </Button>
      </div>
    );
  }

  if (!p6.ok) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Cannot Calculate Settlement</AlertTitle>
          <AlertDescription className="text-xs space-y-2 mt-2">
            <p className="font-medium">{p6.error}</p>
            {p6.blocking_issues && p6.blocking_issues.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-semibold">Blocking items to resolve:</p>
                <ul className="list-disc pl-5 space-y-1 max-h-48 overflow-y-auto">
                  {p6.blocking_issues.map((issue, idx) => (
                    <li key={idx}>
                      Source: <strong>{issue.source || "Discrepancy"}</strong>
                      {issue.booking_id ? ` (Booking: ${issue.booking_id})` : ""}
                      {issue.issue ? ` — ${issue.issue}` : ""}
                      {issue.revenue != null ? ` (Net: ${issue.revenue})` : ""}
                      {issue.booking_count != null ? ` (Bookings: ${issue.booking_count})` : ""}
                      {issue.amount != null ? ` (₹${Number(issue.amount).toLocaleString("en-IN")})` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </AlertDescription>
        </Alert>
        <div className="flex justify-end">
          <Button
            onClick={handleRunPhase6}
            disabled={running}
            variant="outline"
            className="border-violet-600 text-violet-600 hover:bg-violet-50"
          >
            {running ? (
              <>
                <RotateCw className="h-4 w-4 mr-2 animate-spin" />
                Re-running Phase 6...
              </>
            ) : (
              <>
                <RotateCw className="h-4 w-4 mr-2" />
                Re-run Phase 6
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  const { online, offline, settlement } = p6;

  const fmt = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  return (
    <div className="space-y-6">
      {/* Configuration Readout */}
      <div className="flex gap-2 text-xs">
        <Badge variant="outline" className="bg-violet-50 text-violet-700">Partner HMS Online: {online.hms_rate_pct}%</Badge>
        <Badge variant="outline" className="bg-violet-50 text-violet-700">Partner HMS Offline: {offline.hms_rate_pct}%</Badge>
        {settlement.ignore_offline && <Badge variant="secondary">Offline Ignored</Badge>}
      </div>

      {/* Stay-Through Proration Policy Explainer */}
      <div className="rounded-lg border border-violet-100 bg-violet-50/60 p-3.5 text-xs text-slate-700 space-y-1">
        <div className="flex items-center gap-1.5 font-semibold text-violet-900">
          <Info className="h-4 w-4 text-violet-600 flex-shrink-0" />
          <span>Stay-Through & Multi-Month Booking Policy</span>
        </div>
        <p className="text-slate-600 leading-relaxed">
          For bookings spanning across multiple months (e.g. Aug 30 – Oct 3), HMS commission is calculated <strong>strictly on the room-nights occupied within {session.audit_month || "the audit month"}</strong>.
          Gross revenue, OTA fees, and GST are prorated: <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-violet-200 text-violet-800 text-[11px]">Audit Month Nights / Total Stay Nights</span>. You are never billed for nights stayed in preceding or subsequent months.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Online Settlement */}
        <div className="border rounded-md p-4 bg-card shadow-sm">
          <h3 className="text-sm font-semibold mb-3">Online & HMS Direct Revenue</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Gross Sales (Stay-Through Prorated)</span>
              <span>{fmt(online.gross)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Actual Fees (Prorated)</span>
              <span className="text-red-500">-{fmt(online.fees)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">GST Subtracted</span>
              <span className="text-red-500">-(calculated at 5%)</span>
            </div>
            <div className="pt-2 border-t flex justify-between font-medium">
              <span>HMS Commission</span>
              <span className="text-violet-700">{fmt(online.hms_commission)}</span>
            </div>
          </div>
        </div>

        {/* Offline Settlement */}
        <div className="border rounded-md p-4 bg-card shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Offline / Direct Revenue</h3>
            {offline.ignored && (
              <Badge variant="outline" className="text-[11px] bg-slate-100 text-slate-700 border">
                Excluded per partner agreement
              </Badge>
            )}
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Gross Sales (Desk / Walk-ins)</span>
              <span>{fmt(offline.gross)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Actual Fees</span>
              <span className="text-red-500">-{fmt(offline.fees)}</span>
            </div>
            <div className="pt-2 border-t flex justify-between font-medium">
              <span>HMS Commission {offline.ignored ? "(0% — Excluded)" : `(${offline.hms_rate_pct}%)`}</span>
              <span className={offline.ignored ? "text-slate-400 font-mono font-medium" : "text-violet-700"}>
                {offline.ignored ? "₹0.00" : fmt(offline.hms_commission)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Alert */}
      <Alert className="bg-violet-50 border-violet-200">
        <FileText className="h-4 w-4 text-violet-600" />
        <AlertTitle className="text-violet-800">Final Settlement</AlertTitle>
        <AlertDescription className="text-sm mt-2">
          <div className="flex justify-between items-center">
            <span className="text-violet-700">Total amount owed to HMS for {session.audit_month}:</span>
            <span className="text-xl font-bold text-violet-900">{fmt(settlement.total_hms_commission)}</span>
          </div>
        </AlertDescription>
      </Alert>

      {/* Channel Breakdown Table */}
      <div className="border rounded-md p-4 bg-card shadow-sm space-y-3">
        <h4 className="text-sm font-semibold">Channel Settlement Breakdown</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="border-b text-muted-foreground font-medium">
                <th className="py-2 pr-4">Channel / Source</th>
                <th className="py-2 px-3">Classification</th>
                <th className="py-2 px-3 text-right">Gross Sales</th>
                <th className="py-2 px-3 text-right">Actual Fees</th>
                <th className="py-2 px-3 text-right">GST (5%)</th>
                <th className="py-2 px-3 text-right">Settlement Base</th>
                <th className="py-2 pl-3 text-right">HMS Share</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {Object.entries(online.breakdown || {}).map(([src, d]) => (
                <tr key={`online-${src}`} className="hover:bg-muted/30">
                  <td className="py-2 pr-4 font-medium">{src}</td>
                  <td className="py-2 px-3">
                    <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                      {src.toUpperCase().includes("PHONE") || src.toUpperCase().includes("CALL") ? "HMS Direct" : "Online OTA"} ({online.hms_rate_pct}%)
                    </Badge>
                  </td>
                  <td className="py-2 px-3 text-right font-mono">{fmt(d.gross)}</td>
                  <td className="py-2 px-3 text-right font-mono text-red-500">-{fmt(d.fees)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-500">-{fmt(d.gst)}</td>
                  <td className="py-2 px-3 text-right font-mono">{fmt(d.base)}</td>
                  <td className="py-2 pl-3 text-right font-mono font-semibold text-violet-700">{fmt(d.hms_share)}</td>
                </tr>
              ))}
              {Object.entries(offline.breakdown || {}).map(([src, d]) => (
                <tr key={`offline-${src}`} className="hover:bg-muted/30 bg-slate-50/50">
                  <td className="py-2 pr-4 font-medium">{src}</td>
                  <td className="py-2 px-3"><Badge variant="secondary" className="text-[10px]">{offline.ignored ? "Offline (Excluded)" : `Offline (${offline.hms_rate_pct}%)`}</Badge></td>
                  <td className="py-2 px-3 text-right font-mono">{fmt(d.gross)}</td>
                  <td className="py-2 px-3 text-right font-mono text-red-500">-{fmt(d.fees)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-500">-{fmt(d.gst)}</td>
                  <td className="py-2 px-3 text-right font-mono">{fmt(d.base)}</td>
                  <td className="py-2 pl-3 text-right font-mono font-semibold text-slate-400">{offline.ignored ? "₹0.00" : fmt(d.hms_share)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="flex justify-between items-center pt-4 border-t">
        <Button
          onClick={handleRunPhase6}
          disabled={running}
          variant="outline"
          size="sm"
          className="border-violet-600 text-violet-600 hover:bg-violet-50"
        >
          {running ? (
            <>
              <RotateCw className="h-4 w-4 mr-2 animate-spin" />
              Re-calculating...
            </>
          ) : (
            <>
              <RotateCw className="h-4 w-4 mr-2" />
              Re-run Settlement
            </>
          )}
        </Button>
        <Button onClick={downloadPdf} disabled={downloading} variant="outline" className="text-violet-700 border-violet-300 hover:bg-violet-50">
          {downloading ? "Generating PDF..." : "Download PDF Statement"}
        </Button>
      </div>
    </div>
  );
}
